/**
 * 軽タスクリスト（全社共有プール）。
 *
 * 起票時の AI 判定で軽タスク認定され、まだ開発リーダーが設定されていない案件を、起票時刻の
 * 昇順（最古が先頭）で一覧表示する。全メンバーが閲覧できる共有の待機列で、誰でも拾える。
 * エンジニア / PMO team は「軽タスクをリクエストする」ボタンで、自分の所属に合う軽タスクを
 * 起票順に1件セルフ・アサインできる。アサイン済みは下部の「アサイン済み軽タスク」ブロックに出る。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 軽タスクの待機列表示 + セルフ・アサイン導線 + アサイン済み一覧を表示するプロジェクト管理配下の軽タスクリストページ本体
 * @graph-connects useLightTaskPool [calls] 待機列取得フック
 * @graph-connects useAssignedLightTasks [calls] アサイン済み一覧取得フック
 * @graph-connects useClaimLightTask [calls] セルフ・アサイン ミューテーション
 * @graph-connects LightTaskTestabilityCell [calls] テスト可否セル（プルダウン / バッジ）
 */

import { useState, type ReactNode } from 'react';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Modal } from '@cortex/web-design-system/components/ui/Modal';
import {
  isCombatEngineerByTeam,
  PMO_TEAM_NAME,
} from '@cortex/domain-product-innovation-lab-combat-power-scope';
import { canEditLightTaskTestability } from '@cortex/domain-product-innovation-lab-types';
import {
  useLightTaskPool,
  useAssignedLightTasks,
  useClaimLightTask,
  useAuth,
} from '../../../hooks/index.js';
import type {
  LightTaskPoolItem,
  AssignedLightTaskItem,
  LightTaskClaimResult,
} from '../../../types/project.js';
import { LightTaskTestabilityCell } from './LightTaskTestabilityCell.js';
import lightTaskLoadingGif from '../../../assets/design-search-loading.gif';
import { FassychanLoadingOverlay } from '../../ui/FassychanLoadingOverlay/index.js';

/** 起票時刻（ISO 文字列）を YYYY-MM-DD で表示する。取得不能なら "-"。 @graph-connects none */
function formatCreatedAt(createdAt: string | null): string {
  if (!createdAt) return '-';
  return createdAt.slice(0, 10);
}

/** 待機列テーブルのヘッダー（表示順）。 @graph-connects none */
const POOL_COLUMNS = ['起票日', '課題キー', '案件名', 'やりたいこと', 'チーム', 'PL'] as const;
/** アサイン済みテーブルのヘッダー（表示順）。 @graph-connects none */
const ASSIGNED_COLUMNS = [
  '起票日',
  '課題キー',
  '案件名',
  'やりたいこと',
  '開発者',
  'チーム',
  'テスト可否',
] as const;

/** 課題キーの Backlog リンクセル。 @graph-connects none */
function IssueKeyLink({ item }: { item: { issueKey: string; backlogUrl: string } }): ReactNode {
  return (
    <a
      href={item.backlogUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="font-mono text-xs text-[var(--color-accent-primary)] hover:underline"
    >
      {item.issueKey}
    </a>
  );
}

/** リスト取得中のローディング（design成果物検索と同じ turntable GIF）。 @graph-connects none */
function ListLoading(): ReactNode {
  return (
    <Card
      className="flex flex-col items-center justify-center gap-2 px-6 py-12"
      role="status"
      aria-live="polite"
    >
      <img
        src={lightTaskLoadingGif}
        alt=""
        aria-hidden="true"
        className="h-28 w-28 object-contain motion-reduce:hidden sm:h-32 sm:w-32"
      />
      <span className="text-sm text-[var(--color-text-muted)]">読み込み中...</span>
    </Card>
  );
}

/** アサイン結果 → 表示メッセージ（title / body）。 @graph-connects none */
function claimMessage(result: LightTaskClaimResult): { title: string; body: string } {
  if (result.assigned) {
    return {
      title: '自動アサイン完了しました！',
      body: `${result.assigned.issueKey}「${result.assigned.summary}」を ${result.assigned.developer} にアサインしました。案件チャンネルに通知しました。`,
    };
  }
  switch (result.reason) {
    case 'no-match':
      return {
        title: 'アサインできる軽タスクがありません',
        body: '所属に合う未アサインの軽タスクが見つかりませんでした。',
      };
    case 'taken':
      return {
        title: 'すでに取得されていました',
        body: '対象の軽タスクは他の人が先に取得しました。再度お試しください。',
      };
    case 'not-eligible':
      return {
        title: 'アサイン対象外です',
        body: '軽タスクのセルフ・アサイン対象の所属ではありません。',
      };
    default:
      return {
        title: 'アサインに失敗しました',
        body: '処理中にエラーが発生しました。時間をおいて再度お試しください。',
      };
  }
}

/**
 * 軽タスクリストタブ本体。
 * @graph-connects none
 */
export function LightTaskListTab(): ReactNode {
  const { user } = useAuth();
  const pool = useLightTaskPool();
  const assigned = useAssignedLightTasks();
  const claim = useClaimLightTask();
  const [result, setResult] = useState<LightTaskClaimResult | null>(null);
  const [errorOpen, setErrorOpen] = useState(false);

  // エンジニア（PDG/ACE の team）または PMO team のみボタンを表示する。
  const canClaim =
    isCombatEngineerByTeam({ name: user?.nickName ?? null, team: user?.teamName ?? null }) ||
    user?.teamName === PMO_TEAM_NAME;

  // テスト可否の編集は PMOチーム / PI Div の team_leader / 管理者のみ（API 側でも同じ述語で enforce）。
  const canEditTestability = canEditLightTaskTestability({
    portalRoles: user?.portalRoles,
    division: user?.division,
    teamName: user?.teamName,
  });

  const handleClaim = (): void => {
    claim.mutate(undefined, {
      onSuccess: (res) => {
        setResult(res);
      },
      onError: () => {
        setErrorOpen(true);
      },
    });
  };

  const modal = result ? claimMessage(result) : null;

  return (
    <div>
      {claim.isPending && (
        <FassychanLoadingOverlay
          label="自動アサイン処理中..."
          portal
          zIndexClassName="z-[var(--z-modal)]"
        />
      )}

      <div className="mb-4 flex items-center justify-between gap-3">
        <p className="text-sm text-[var(--color-text-secondary)]">
          AI 判定で軽タスクと認定され、開発リーダーが未設定の案件です（起票が早い順）。
        </p>
        {canClaim && (
          <Button
            variant="primary"
            onClick={handleClaim}
            disabled={claim.isPending}
            className="shrink-0"
          >
            軽タスクをリクエストする
          </Button>
        )}
      </div>

      {pool.isLoading ? (
        <ListLoading />
      ) : (
        <>
          {/* デスクトップテーブル（待機列） */}
          <Card className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--color-border)]">
                  {POOL_COLUMNS.map((col) => (
                    <th
                      key={col}
                      className="px-4 py-3 text-left font-medium text-[var(--color-text-secondary)]"
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pool.isError ? (
                  <tr>
                    <td
                      colSpan={POOL_COLUMNS.length}
                      className="px-4 py-12 text-center text-[var(--color-error)]"
                    >
                      軽タスクリストの取得に失敗しました
                    </td>
                  </tr>
                ) : (pool.data?.items.length ?? 0) === 0 ? (
                  <tr>
                    <td
                      colSpan={POOL_COLUMNS.length}
                      className="px-4 py-12 text-center text-[var(--color-text-muted)]"
                    >
                      対象の軽タスクはありません
                    </td>
                  </tr>
                ) : (
                  pool.data?.items.map((item) => <LightTaskRow key={item.issueKey} item={item} />)
                )}
              </tbody>
            </table>
          </Card>

          {/* モバイルカード（待機列） */}
          <div className="block md:hidden space-y-3">
            {pool.isError ? (
              <Card className="p-4 text-center text-[var(--color-error)]">
                軽タスクリストの取得に失敗しました
              </Card>
            ) : (pool.data?.items.length ?? 0) === 0 ? (
              <Card className="p-4 text-center text-[var(--color-text-muted)]">
                対象の軽タスクはありません
              </Card>
            ) : (
              pool.data?.items.map((item) => <LightTaskCard key={item.issueKey} item={item} />)
            )}
          </div>
        </>
      )}

      {/* アサイン済み軽タスク ブロック */}
      <AssignedLightTaskBlock
        isLoading={assigned.isLoading}
        isError={assigned.isError}
        items={assigned.data?.items ?? []}
        canEditTestability={canEditTestability}
      />

      {/* アサイン結果ダイアログ */}
      <Modal
        open={modal !== null}
        onClose={() => {
          setResult(null);
        }}
        title={modal?.title}
        description={modal?.body}
        size="sm"
        footer={
          <Button
            variant="primary"
            onClick={() => {
              setResult(null);
            }}
          >
            OK
          </Button>
        }
      />

      {/* 通信エラーダイアログ */}
      <Modal
        open={errorOpen}
        onClose={() => {
          setErrorOpen(false);
        }}
        title="アサインに失敗しました"
        description="通信エラーが発生しました。時間をおいて再度お試しください。"
        size="sm"
        footer={
          <Button
            variant="primary"
            onClick={() => {
              setErrorOpen(false);
            }}
          >
            OK
          </Button>
        }
      />
    </div>
  );
}

/** アサイン済み軽タスク ブロック（待機列の下・全メンバー閲覧可）。 @graph-connects none */
function AssignedLightTaskBlock({
  isLoading,
  isError,
  items,
  canEditTestability,
}: {
  isLoading: boolean;
  isError: boolean;
  items: AssignedLightTaskItem[];
  canEditTestability: boolean;
}): ReactNode {
  return (
    <div className="mt-8">
      <h3 className="mb-3 text-sm font-medium text-[var(--color-text-secondary)]">
        アサイン済み軽タスク（{items.length.toString()}件）
      </h3>
      {isLoading ? (
        <Card className="p-6 text-center text-sm text-[var(--color-text-muted)]">
          読み込み中...
        </Card>
      ) : (
        <>
          {/* デスクトップテーブル */}
          <Card className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--color-border)]">
                  {ASSIGNED_COLUMNS.map((col) => (
                    <th
                      key={col}
                      className="px-4 py-3 text-left font-medium text-[var(--color-text-secondary)]"
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {isError ? (
                  <tr>
                    <td
                      colSpan={ASSIGNED_COLUMNS.length}
                      className="px-4 py-8 text-center text-[var(--color-error)]"
                    >
                      アサイン済み軽タスクの取得に失敗しました
                    </td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td
                      colSpan={ASSIGNED_COLUMNS.length}
                      className="px-4 py-8 text-center text-[var(--color-text-muted)]"
                    >
                      アサイン済みの軽タスクはありません
                    </td>
                  </tr>
                ) : (
                  items.map((item) => (
                    <AssignedRow
                      key={item.issueKey}
                      item={item}
                      canEditTestability={canEditTestability}
                    />
                  ))
                )}
              </tbody>
            </table>
          </Card>

          {/* モバイルカード */}
          <div className="block md:hidden space-y-3">
            {isError ? (
              <Card className="p-4 text-center text-[var(--color-error)]">
                アサイン済み軽タスクの取得に失敗しました
              </Card>
            ) : items.length === 0 ? (
              <Card className="p-4 text-center text-[var(--color-text-muted)]">
                アサイン済みの軽タスクはありません
              </Card>
            ) : (
              items.map((item) => (
                <AssignedCard
                  key={item.issueKey}
                  item={item}
                  canEditTestability={canEditTestability}
                />
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}

/** 待機列のデスクトップ 1 行。 @graph-connects none */
function LightTaskRow({ item }: { item: LightTaskPoolItem }): ReactNode {
  return (
    <tr className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-hover)]">
      <td className="whitespace-nowrap px-4 py-3 text-xs text-[var(--color-text-muted)]">
        {formatCreatedAt(item.createdAt)}
      </td>
      <td className="px-4 py-3">
        <IssueKeyLink item={item} />
      </td>
      <td className="max-w-xs truncate px-4 py-3">{item.summary}</td>
      <td className="max-w-xs truncate px-4 py-3 text-xs" title={item.purpose ?? undefined}>
        {item.purpose ?? '-'}
      </td>
      <td className="px-4 py-3 text-xs">{item.team ?? '-'}</td>
      <td className="px-4 py-3 text-xs">{item.pl ?? '-'}</td>
    </tr>
  );
}

/** 待機列のモバイル 1 カード。 @graph-connects none */
function LightTaskCard({ item }: { item: LightTaskPoolItem }): ReactNode {
  return (
    <Card className="p-4">
      <div className="mb-2 flex items-center justify-between">
        <IssueKeyLink item={item} />
        <span className="text-xs text-[var(--color-text-muted)]">
          {formatCreatedAt(item.createdAt)}
        </span>
      </div>
      <div className="truncate text-sm">{item.summary}</div>
      {item.purpose && (
        <div className="mt-1 line-clamp-2 text-xs text-[var(--color-text-secondary)]">
          やりたいこと: {item.purpose}
        </div>
      )}
      <div className="mt-1 text-xs text-[var(--color-text-muted)]">
        {(item.team ?? '-') + ' ・ PL: ' + (item.pl ?? '-')}
      </div>
    </Card>
  );
}

/** アサイン済みのデスクトップ 1 行。 @graph-connects none */
function AssignedRow({
  item,
  canEditTestability,
}: {
  item: AssignedLightTaskItem;
  canEditTestability: boolean;
}): ReactNode {
  return (
    <tr className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-hover)]">
      <td className="whitespace-nowrap px-4 py-3 text-xs text-[var(--color-text-muted)]">
        {formatCreatedAt(item.createdAt)}
      </td>
      <td className="px-4 py-3">
        <IssueKeyLink item={item} />
      </td>
      <td className="max-w-xs truncate px-4 py-3">{item.summary}</td>
      <td className="max-w-xs truncate px-4 py-3 text-xs" title={item.purpose ?? undefined}>
        {item.purpose ?? '-'}
      </td>
      <td className="px-4 py-3 text-xs">{item.developer ?? '-'}</td>
      <td className="px-4 py-3 text-xs">{item.team ?? '-'}</td>
      <td className="px-4 py-3 text-xs">
        <LightTaskTestabilityCell item={item} canEdit={canEditTestability} />
      </td>
    </tr>
  );
}

/** アサイン済みのモバイル 1 カード。 @graph-connects none */
function AssignedCard({
  item,
  canEditTestability,
}: {
  item: AssignedLightTaskItem;
  canEditTestability: boolean;
}): ReactNode {
  return (
    <Card className="p-4">
      <div className="mb-2 flex items-center justify-between">
        <IssueKeyLink item={item} />
        <span className="text-xs text-[var(--color-text-muted)]">
          {formatCreatedAt(item.createdAt)}
        </span>
      </div>
      <div className="truncate text-sm">{item.summary}</div>
      {item.purpose && (
        <div className="mt-1 line-clamp-2 text-xs text-[var(--color-text-secondary)]">
          やりたいこと: {item.purpose}
        </div>
      )}
      <div className="mt-1 text-xs text-[var(--color-text-muted)]">
        {'開発者: ' + (item.developer ?? '-') + ' ・ ' + (item.team ?? '-')}
      </div>
      <div className="mt-2 flex items-center gap-2">
        <span className="text-xs text-[var(--color-text-muted)]">テスト可否:</span>
        <LightTaskTestabilityCell item={item} canEdit={canEditTestability} />
      </div>
    </Card>
  );
}
